import { Box, Tab, TablePagination, Tabs } from '@mui/material';
import type React from 'react';
import { type FC, memo, useCallback, useEffect } from 'react';
import { TableBody } from '@aws-amplify/ui-react';
import useMetrics from '../hooks/useMetrics';
import { FormProvider, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash-es';
import MetricsModifyButtons from './MetricsModifyButtons';
import MetricsEmptyState from './MetricsComponents';
import { useMe } from 'features/users';
import { Navigate } from 'react-router';
import ProgressSkeleton from 'components/layout/ProgressSkeleton';
import { editMetricsSchema } from '../validation/editMetrics.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import useProjectConditions from '../hooks/useProjectConditions';
import useAccessModify from '../hooks/useAccessModify';
import TabPanel from '../../../components/common/TabPanel';
import MetricTableContainer from './MetricTableContainer';
import EditMetricValueList from './EditMetricValueList';
import MetricValueTableHead from './MetricValueTableHead';
import MetricDescriptionTableHead from './MetricDescriptionTableHead';
import EditMetricDescriptionList from './EditMetricDescriptionList';
import type { TabValues } from '../hooks/useTabParam';
import useTabParam from '../hooks/useTabParam';
import type { MetricsFormState } from '../types';

const MetricTabs = memo(() => {
  const [tabParam, setTabParam] = useTabParam();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: TabValues) => {
      setTabParam(value);
    },
    [setTabParam]
  );

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 6,
        borderBottom: 1,
        borderColor: 'divider',
        width: '100%',
        height: 48,
      }}
    >
      <Tabs value={tabParam} onChange={handleChange}>
        <Tab label="Values" value="values" />
        <Tab label="Details" value="details" />
      </Tabs>
    </Box>
  );
});
MetricTabs.displayName = 'MetricTabs';

const MetricsEdit: FC = () => {
  const { isPending } = useMe();
  const [tabParam] = useTabParam();

  const { isNotAssigned } = useProjectConditions();
  const { accessMetrics } = useAccessModify();
  const {
    tableHead,
    tableRows,
    meta: { count },
    perPage,
    page,
    handlePageChange,
    handleRowsPerPageChange,
  } = useMetrics();

  const formMethods = useForm<MetricsFormState>({
    // @ts-expect-error types correction
    resolver: yupResolver(editMetricsSchema),
    defaultValues: { items: tableRows },
  });

  useEffect(() => {
    return () => {
      formMethods.reset();
    };
  }, [formMethods]);

  if (isPending)
    return (
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          flexGrow: 1,
          width: '100%',
          height: '100%',
        }}
      >
        <ProgressSkeleton />
      </Box>
    );

  if ((!accessMetrics.accessModify && !isPending) || isNotAssigned) {
    return <Navigate to={'/metrics?project=all_projects'} />;
  }

  if (isEmpty(tableRows)) {
    return <MetricsEmptyState />;
  }

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <MetricTabs />

        <TabPanel value={String(tabParam)} panelValue="values">
          <MetricTableContainer>
            <MetricValueTableHead columns={tableHead} />
            <TableBody style={{ height: '100%' }}>
              <EditMetricValueList items={tableRows} />
            </TableBody>
          </MetricTableContainer>
        </TabPanel>
        <TabPanel value={String(tabParam)} panelValue="details">
          <MetricTableContainer>
            <MetricDescriptionTableHead />
            <TableBody style={{ height: '100%' }}>
              <EditMetricDescriptionList items={tableRows} />
            </TableBody>
          </MetricTableContainer>
        </TabPanel>

        <Box sx={{ flex: 1, maxHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <TablePagination
            labelRowsPerPage="Rows per page:"
            component="div"
            count={count}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={perPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100]}
            sx={{
              position: 'relative',
              zIndex: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              flexGrow: 1,
              minHeight: 54,
              maxHeight: 54,
              backgroundColor: 'white',
            }}
          />
          {!isEmpty(tableRows) && <MetricsModifyButtons />}
        </Box>
      </Box>
    </FormProvider>
  );
};

export default MetricsEdit;
